/**
 * CMS Content Configuration
 *
 * This file serves as the DEFAULT/FALLBACK content.
 * Content is fetched from Shopify metafields first, then falls back to these values.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * HOW TO MANAGE CONTENT IN SHOPIFY ADMIN
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * STEP 1: Set up Shop Metafields (Global Settings)
 * ─────────────────────────────────────────────────
 * Go to: Settings → Custom data → Shop → Add definition
 *
 * Create these metafields (namespace: "custom"):
 *
 * | Key                      | Type                  | Description                    |
 * |--------------------------|----------------------|--------------------------------|
 * | phone                    | Single line text     | Contact phone number           |
 * | email                    | Single line text     | Contact email address          |
 * | address                  | Multi-line text      | Business address               |
 * | announcement_text        | Single line text     | Header announcement banner     |
 * | free_shipping_threshold  | Integer              | Free shipping minimum (e.g. 50)|
 * | business_hours           | JSON                 | {"weekdays": "9am-5:30pm", ... }|
 * | social_links             | JSON                 | {"linkedin": "url", ...}       |
 *
 * STEP 2: Set up Page Metafields (Per-Page Content)
 * ─────────────────────────────────────────────────
 * Go to: Settings → Custom data → Pages → Add definition
 *
 * Create these metafields (namespace: "custom"):
 *
 * | Key              | Type                  | Description                    |
 * |------------------|----------------------|--------------------------------|
 * | hero_title       | Single line text     | Page hero headline             |
 * | hero_subtitle    | Multi-line text      | Page hero description          |
 * | hero_badge       | Single line text     | Badge text (e.g. "Specialist UK Retailer") |
 * | hero_image       | File (image)         | Hero background image          |
 * | stats            | JSON                 | Array of stat objects          |
 * | faqs             | JSON                 | Array of FAQ objects           |
 * | sections         | JSON                 | Custom page sections           |
 * | cta_title        | Single line text     | Call-to-action headline        |
 * | cta_subtitle     | Single line text     | Call-to-action description     |
 * | cta_button_text  | Single line text     | CTA button label               |
 * | cta_button_link  | URL                  | CTA button destination         |
 *
 * STEP 3: Create Pages with Content
 * ──────────────────────────────────
 * Go to: Online Store → Pages → Add page
 *
 * Create pages with these handles (URL slugs):
 * - about, support, contact, faq, shipping, returns, warranty, privacy, terms
 *
 * Fill in the metafields for each page to customize content.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * JSON EXAMPLES FOR METAFIELDS
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * stats metafield:
 * [
 *   { "value": "Specialist", "label": "UK Retailer", "description": "Professional headsets" },
 *   { "value": "15+", "label": "Years Experience", "description": "Industry expertise" }
 * ]
 *
 * faqs metafield:
 * [
 *   {
 *     "category": "Orders",
 *     "questions": [
 *       { "q": "How long does delivery take?", "a": "2-3 business days..." }
 *     ]
 *   }
 * ]
 *
 * business_hours metafield:
 * {
 *   "weekdays": "9:00 AM - 5:30 PM",
 *   "weekends": "Closed",
 *   "timezone": "GMT"
 * }
 *
 * social_links metafield:
 * {
 *   "linkedin": "https://linkedin.com/company/headsetsco",
 *   "twitter": "https://twitter.com/headsetsco"
 * }
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * FALLBACK CONTENT BELOW (used when Shopify metafields are not set)
 * ═══════════════════════════════════════════════════════════════════════════════
 */

// ===========================================
// SITE-WIDE SETTINGS
// ===========================================
export const siteConfig = {
  name: 'IT Remarketing',
  tagline: 'Secure. Simple. Sustainable.',
  description: 'Specialist retailer of professional communication headsets',

  contact: {
    phone: '020 4591 5420',
    email: 'sales@itremarketing.co.uk',
    address: '18 Church Street, Warnham, Horsham, West Sussex, RH12 3QW',
  },

  social: {} as Record<string, string>,

  businessHours: {
    weekdays: '9am - 5pm',
    weekends: 'Closed',
    timezone: 'GMT',
  },
};

// ===========================================
// ABOUT PAGE
// ===========================================
export const aboutContent = {
  hero: {
    badge: 'Specialist UK Retailer',
    title: 'We\'re on a mission to make communication crystal clear',
    subtitle: 'Your specialist source for professional communication headsets from Jabra, Poly, and Axtel — with free next-day delivery and 30-day returns.',
    image: '/images/about-hero.jpg',
  },

  stats: [
    { value: 'Specialist', label: 'UK Retailer', description: 'Professional headsets' },
    { value: '30-Day', label: 'Free Returns', description: 'Hassle-free' },
    { value: '24hr', label: 'Fast Delivery', description: 'Next-day shipping' },
  ],

  story: {
    title: 'Our Story',
    paragraphs: [
      'IT Remarketing Limited was established to make it easy for businesses to find the perfect communication headsets. We\'re a specialist retailer focused on professional audio solutions.',
      'We stock Jabra, Poly, and Axtel headsets, bringing you genuine products at competitive prices with full manufacturer warranties.',
      'Whether you\'re working from home or outfitting an office, we\'re here to help you find the right headset for your needs.',
    ],
  },

  values: [
    {
      icon: 'quality',
      title: 'Quality First',
      description: 'We only stock genuine products, backed by full manufacturer warranties.',
    },
    {
      icon: 'expertise',
      title: 'Expert Advice',
      description: 'Our team can help you choose the right headset for your specific work environment and needs.',
    },
    {
      icon: 'service',
      title: 'Customer Obsessed',
      description: 'From pre-sales advice to post-purchase support, we\'re with you every step of the way.',
    },
    {
      icon: 'speed',
      title: 'Lightning Fast',
      description: 'Order before 3pm and get next-day delivery. We know you need your headsets yesterday.',
    },
  ],


  cta: {
    title: 'Ready to upgrade your audio?',
    subtitle: 'Browse our collection or speak with an expert today.',
    primaryButton: { text: 'Shop Headsets', href: '/collections/all-products' },
    secondaryButton: { text: 'Talk to Us', href: '/contact' },
  },
};

// ===========================================
// SUPPORT PAGE
// ===========================================
export const supportContent = {
  hero: {
    title: 'How can we help?',
    subtitle: 'Find answers, get expert advice, or reach out to our friendly support team.',
    searchPlaceholder: 'Search for help...',
  },

  quickActions: [
    {
      icon: 'setup',
      title: 'Setup Guides',
      description: 'Step-by-step instructions to get your headset working perfectly',
      href: '/setup',
      color: 'blue',
    },
    {
      icon: 'compatibility',
      title: 'Compatibility Checker',
      description: 'Verify your headset works with your devices and software',
      href: '/compatibility',
      color: 'purple',
    },
    {
      icon: 'warranty',
      title: 'Warranty Claims',
      description: 'Register your product or submit a warranty claim',
      href: '/warranty',
      color: 'green',
    },
    {
      icon: 'returns',
      title: 'Returns & Refunds',
      description: 'Easy 30-day returns with free return shipping',
      href: '/returns',
      color: 'orange',
    },
  ],

  faqs: [
    {
      category: 'Orders & Delivery',
      questions: [
        {
          q: 'How long does delivery take?',
          a: 'Standard delivery is 2-3 business days. Next-day delivery is available for orders placed before 3pm (free on orders over £50).',
        },
        {
          q: 'Can I track my order?',
          a: 'Yes! You\'ll receive a tracking link via email once your order ships. You can also track orders in your account dashboard.',
        },
        {
          q: 'Do you deliver internationally?',
          a: 'Currently we only deliver within the UK. We\'re working on expanding to Europe soon.',
        },
      ],
    },
    {
      category: 'Products & Compatibility',
      questions: [
        {
          q: 'Will this headset work with Microsoft Teams?',
          a: 'Look for the "Teams Certified" badge on our products. These headsets are officially certified for Microsoft Teams with plug-and-play functionality.',
        },
        {
          q: 'What\'s the difference between USB and Bluetooth headsets?',
          a: 'USB headsets offer reliable, always-on connectivity ideal for desk work. Bluetooth headsets provide freedom to move around and often work with multiple devices.',
        },
        {
          q: 'How do I know which headset is right for me?',
          a: 'Use our Headset Finder tool or contact our team for personalized recommendations based on your work environment and needs.',
        },
      ],
    },
    {
      category: 'Returns & Warranty',
      questions: [
        {
          q: 'What\'s your return policy?',
          a: 'We offer 30-day hassle-free returns. Items must be in original condition with packaging. Contact us for a prepaid return label.',
        },
        {
          q: 'How long is the warranty?',
          a: 'All products come with a minimum 2-year manufacturer warranty. Some premium products include extended warranty options.',
        },
        {
          q: 'My headset stopped working. What do I do?',
          a: 'First, check our troubleshooting guides. If the issue persists, contact support with your order number and we\'ll arrange a replacement or repair.',
        },
      ],
    },
  ],

  contactOptions: [
    {
      icon: 'phone',
      title: 'Phone Support',
      description: '020 4591 5420',
      availability: 'Mon-Fri 9am-5pm',
      action: { type: 'link', href: 'tel:02045915420', label: 'Call Now' },
    },
    {
      icon: 'email',
      title: 'Email Us',
      description: 'sales@itremarketing.co.uk',
      availability: 'Response within 24hrs',
      action: { type: 'link', href: '/contact', label: 'Send Email' },
    },
  ],

  popularArticles: [] as { title: string; href: string }[],
};

// ===========================================
// CONTACT PAGE
// ===========================================
export const contactContent = {
  hero: {
    title: 'Get in Touch',
    subtitle: 'Have a question or need expert advice? We\'re here to help you find the perfect audio solution.',
  },

  contactMethods: [
    {
      icon: 'phone',
      title: 'Call Us',
      value: '020 4591 5420',
      description: 'Mon–Fri, 9am–5pm',
      href: 'tel:02045915420',
      availability: 'Mon-Fri 9am-5pm',
    },
    {
      icon: 'email',
      title: 'Email Us',
      value: 'sales@itremarketing.co.uk',
      description: 'We reply within 24 hours',
      href: 'mailto:sales@itremarketing.co.uk',
      availability: 'Anytime',
    },
  ],

  offices: [
    {
      city: 'Warnham, Horsham',
      type: 'Registered Office',
      address: 'Swan Ken, Broadbridge Heath Road\nWarnham, Horsham, West Sussex, RH12 3PA',
      phone: '020 4591 5420',
    },
    {
      city: 'Warnham, Horsham',
      type: 'Trading Address',
      address: '18 Church Street\nWarnham, Horsham, West Sussex, RH12 3QW',
      phone: '020 4591 5420',
    },
  ],

  formFields: {
    title: 'Send us a message',
    subtitle: 'Fill out the form below and we\'ll get back to you within 24 hours.',
    submitButton: 'Send Message',
    successMessage: 'Thanks for your message! We\'ll be in touch soon.',
  },

  departments: [
    { value: 'sales', label: 'Sales Enquiry' },
    { value: 'support', label: 'Technical Support' },
    { value: 'returns', label: 'Returns & Refunds' },
    { value: 'wholesale', label: 'Wholesale / B2B' },
    { value: 'other', label: 'Other' },
  ],
};

// ===========================================
// SHIPPING PAGE
// ===========================================
export const shippingContent = {
  hero: {
    badge: 'Free Next-Day Delivery over £75',
    title: 'Fast, Reliable Delivery',
    subtitle: 'Get your headsets when you need them with our premium delivery options.',
  },

  highlights: [
    { icon: 'truck', title: 'Free Next-Day', description: 'On orders over £75' },
    { icon: 'shield', title: 'Fully Tracked', description: 'Real-time updates' },
    { icon: 'box', title: 'Secure Packaging', description: 'Products arrive safe' },
  ],

  options: [
    {
      name: 'Standard Delivery',
      price: '£4.99',
      time: '2-3 business days',
      description: 'Reliable tracked delivery via Royal Mail',
      features: ['Full tracking', 'Signature on delivery', 'Up to 2 delivery attempts'],
    },
    {
      name: 'Next-Day Delivery',
      price: '£6.99',
      freeOver: 50,
      time: 'Next business day',
      description: 'Order before 3pm for next-day delivery',
      features: ['Guaranteed next-day', 'Full tracking', 'Free over £50', 'Priority handling'],
      recommended: true,
    },
  ],

  cutoffTimes: {
    nextDay: '3:00 PM',
    timezone: 'GMT',
  },

  faqs: [
    {
      q: 'What happens if I miss my delivery?',
      a: 'The courier will attempt redelivery the next business day or leave your parcel at a local collection point.',
    },
    {
      q: 'Can I change my delivery address?',
      a: 'Yes, contact us before dispatch and we can update your delivery address.',
    },
    {
      q: 'Do you deliver to PO Boxes?',
      a: 'Unfortunately, we cannot deliver to PO Boxes. Please provide a physical address.',
    },
  ],
};

// ===========================================
// WARRANTY PAGE
// ===========================================
export const warrantyContent = {
  hero: {
    badge: '2-Year Warranty Included',
    title: 'Protected by Our Promise',
    subtitle: 'Every product comes with comprehensive warranty coverage for your peace of mind.',
  },

  coverage: {
    included: [
      'Manufacturing defects',
      'Component failures under normal use',
      'Electronic malfunctions',
      'Battery degradation (first year)',
      'Connectivity issues',
    ],
    excluded: [
      'Physical damage from drops or impacts',
      'Water damage (unless water-resistant rated)',
      'Normal wear and tear (ear cushions, cables)',
      'Damage from improper use',
      'Unauthorized modifications',
    ],
  },

  claimSteps: [
    {
      step: 1,
      title: 'Contact Support',
      description: 'Email us with your order number and description of the issue',
    },
    {
      step: 2,
      title: 'Diagnosis',
      description: 'Our team will help diagnose the issue and confirm warranty eligibility',
    },
    {
      step: 3,
      title: 'Return',
      description: 'We\'ll send a prepaid return label for you to ship the product back',
    },
    {
      step: 4,
      title: 'Resolution',
      description: 'Receive a replacement or repair within 5-7 business days',
    },
  ],

  extendedWarranty: {
    available: true,
    options: [
      { years: 3, price: '£19.99' },
      { years: 5, price: '£34.99' },
    ],
  },
};

// ===========================================
// RETURNS PAGE
// ===========================================
export const returnsContent = {
  hero: {
    badge: '30-Day Returns',
    title: 'Hassle-Free Returns',
    subtitle: 'Changed your mind? No problem. We make returns simple and stress-free.',
  },

  policy: {
    days: 30,
    freeReturns: true,
    conditions: [
      'Items must be returned within 30 days of delivery',
      'Products must be in original, unused condition',
      'All original packaging and accessories included',
      'Proof of purchase required',
    ],
  },

  process: [
    {
      step: 1,
      title: 'Request Return',
      description: 'Log into your account or email us with your order number',
      icon: 'form',
    },
    {
      step: 2,
      title: 'Print Label',
      description: 'We\'ll email you a free prepaid return label',
      icon: 'print',
    },
    {
      step: 3,
      title: 'Ship It Back',
      description: 'Drop off at any courier collection point',
      icon: 'ship',
    },
    {
      step: 4,
      title: 'Get Refunded',
      description: 'Refund processed within 5-7 business days',
      icon: 'money',
    },
  ],

  refundInfo: {
    processingTime: '5-7 business days',
    method: 'Original payment method',
    shippingRefund: 'Standard shipping refunded for faulty items only',
  },
};

// ===========================================
// FAQ PAGE
// ===========================================
export const faqContent = {
  hero: {
    title: 'Frequently Asked Questions',
    subtitle: 'Find quick answers to common questions about our products and services.',
  },

  categories: [
    {
      name: 'Ordering',
      icon: 'cart',
      questions: [
        { q: 'How do I place an order?', a: 'Simply browse our products, add items to your cart, and proceed to checkout. You can pay with card, PayPal, or Apple Pay.' },
        { q: 'Can I modify my order after placing it?', a: 'Contact us immediately if you need to modify your order. We can make changes before the order is dispatched.' },
        { q: 'Is my payment information secure?', a: 'Absolutely. We use bank-level encryption and never store your full card details. All payments are processed through PCI-compliant providers.' },
      ],
    },
    {
      name: 'Products',
      icon: 'headset',
      questions: [
        { q: 'Are your products genuine?', a: 'Yes, every product we sell is 100% genuine with full manufacturer warranty.' },
        { q: 'How do I choose the right headset?', a: 'Consider your work environment, connection needs (USB/Bluetooth), and features like noise cancellation. Our team can provide personalized recommendations.' },
        { q: 'Do you sell refurbished products?', a: 'Yes, we offer certified refurbished headsets at significant savings. All refurbished items are thoroughly tested and come with a 1-year warranty.' },
      ],
    },
    {
      name: 'Technical',
      icon: 'settings',
      questions: [
        { q: 'How do I update my headset firmware?', a: 'Most manufacturers provide software (like Jabra Direct or Poly Lens) to update firmware. Check our setup guides for specific instructions.' },
        { q: 'My headset isn\'t connecting. What should I do?', a: 'Try resetting your headset, updating drivers, and re-pairing. If issues persist, contact our support team for assistance.' },
        { q: 'Can I use one headset with multiple devices?', a: 'Many Bluetooth headsets support multipoint connection, allowing you to connect to 2+ devices simultaneously. Check the product specifications.' },
      ],
    },
  ],
};

export default {
  site: siteConfig,
  about: aboutContent,
  support: supportContent,
  contact: contactContent,
  shipping: shippingContent,
  warranty: warrantyContent,
  returns: returnsContent,
  faq: faqContent,
};
